import { faFile, faLink } from '@fortawesome/free-solid-svg-icons'
import { getIconFileType } from 'utils/helpers'

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp', 'avif']
const VIDEO_EXTS = ['mp4', 'webm', 'ogv', 'mov', 'm4v']
const AUDIO_EXTS = ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac']
const OFFICE_EXTS = ['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']
// Plain text the browser can't reliably show inside an iframe (csv downloads, etc.),
// so the viewer fetches and renders them itself.
const TEXT_EXTS = ['csv', 'tsv', 'md', 'log', 'yml', 'yaml']
// Text the browser renders natively inside an iframe.
const NATIVE_FRAME_EXTS = ['txt', 'json', 'xml']
const HTML_EXTS = ['html', 'htm']

// Files uploaded through the library live on CloudFront; everything else is a pasted URL.
export const isUploadedFile = (path = '') =>
  !!path &&
  (path.includes('cloudfront') || path.includes('destination-kiwi-uploads'))

// Lower-cased extension of a path/URL, ignoring query string, hash and bare domains.
export const getFileExtension = (path = '') => {
  if (!path) return ''
  let pathname = String(path)
  try {
    pathname = new URL(path).pathname
  } catch (e) {
    pathname = String(path).split('#')[0].split('?')[0]
  }
  const lastSegment = pathname.split('/').pop() || ''
  return lastSegment.includes('.') ? lastSegment.split('.').pop().toLowerCase() : ''
}

// How the in-app viewer (UrlModal) should render this path.
export const getFileKind = (path = '') => {
  const ext = getFileExtension(path)
  if (ext === 'pdf') return 'pdf'
  if (IMAGE_EXTS.includes(ext)) return 'image'
  if (VIDEO_EXTS.includes(ext)) return 'video'
  if (AUDIO_EXTS.includes(ext)) return 'audio'
  if (OFFICE_EXTS.includes(ext)) return 'office'
  if (TEXT_EXTS.includes(ext)) return 'text'
  if (HTML_EXTS.includes(ext)) return 'html'
  if (NATIVE_FRAME_EXTS.includes(ext)) return 'frame'
  // Uploaded file we don't know how to render (zip, rar, ...): offer a download.
  if (isUploadedFile(path)) return 'download'
  // Any other pasted URL is a web page.
  return 'web'
}

// Turns YouTube / Vimeo watch links into embeddable URLs (the watch pages refuse to be framed).
export const getEmbedUrl = (url = '') => {
  try {
    const parsed = new URL(url)
    const host = parsed.hostname.replace(/^www\./, '')

    if (host === 'youtu.be' && parsed.pathname.length > 1) {
      return `https://www.youtube.com/embed/${parsed.pathname.slice(1)}`
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const videoId = parsed.searchParams.get('v')
      if (parsed.pathname === '/watch' && videoId) {
        return `https://www.youtube.com/embed/${videoId}`
      }
      const match = parsed.pathname.match(/^\/(shorts|live)\/([^/]+)/)
      if (match) {
        return `https://www.youtube.com/embed/${match[2]}`
      }
    }

    if (host === 'vimeo.com') {
      const match = parsed.pathname.match(/^\/(\d+)/)
      if (match) {
        return `https://player.vimeo.com/video/${match[1]}`
      }
    }
  } catch (e) {
    // not a parsable URL, fall through
  }
  return url
}

// Icon shown on library cards for a file.
export const getItemIcon = (item) => {
  const path = item?.path
  if (!path) return faFile
  const ext = getFileExtension(path)
  if (ext === 'pdf' || isUploadedFile(path)) {
    return getIconFileType(ext)
  }
  return faLink
}

// File flagged as "open in new tab". Rows where the column is NULL keep the old behaviour (new tab).
export const opensInNewTab = (item) => item?.new_tab !== 0

// Hides the browser PDF toolbar when print/download is not allowed for this file.
export const getPdfToolbarSuffix = (item, pdfjsDownloadAndPrint) =>
  !pdfjsDownloadAndPrint &&
  item?.type === 'file' &&
  getFileExtension(item?.path) === 'pdf' &&
  item?.show_print_download === 0
    ? '#toolbar=0'
    : ''
